import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import "./Slider.css";

const sliderData = [
  {
    id: 1,
    image: "demo.webp",
    label: "Legal Excellence",
    line1: "Trusted",
    line2: "Legal Advisors",
    body: "Our team of experienced lawyers is committed to delivering reliable legal solutions for individuals and businesses with unwavering dedication.",
    stats: [
      { num: "25+", label: "Years" },
      { num: "1.2k", label: "Cases" },
      { num: "98%", label: "Success" },
    ],
  },
  {
    id: 2,
    image: "demo2.webp",
    label: "Justice & Integrity",
    line1: "Justice &",
    line2: "Integrity",
    body: "We uphold justice with integrity and provide personalized representation designed to protect your rights at every critical stage.",
    stats: [
      { num: "500+", label: "Clients" },
      { num: "8k", label: "Cases" },
      { num: "100%", label: "Committed" },
    ],
  },
  {
    id: 3,
    image: "demo3.webp",
    label: "Expert Counsel",
    line1: "Expert",
    line2: "Legal Support",
    body: "From consultations to courtroom advocacy, our seasoned attorneys stand beside you every step of the way with clarity and strength.",
    stats: [
      { num: "3k+", label: "Verdicts" },
      { num: "50+", label: "Attorneys" },
      { num: "99%", label: "Satisfaction" },
    ],
  },
];

const CIRCUMFERENCE = 125.6;

const Slider = () => {
  const [displayed, setDisplayed] = useState(0);
  const [next, setNext] = useState(null);
  const [animating, setAnimating] = useState(false);

  const bgRef = useRef(null);
  const eyebrowRef = useRef(null);
  const headingRef = useRef(null);
  const bodyRef = useRef(null);
  const ctasRef = useRef(null);
  const barRef = useRef(null);
  const ringRef = useRef(null);
  const progTween = useRef(null);
  const curRef = useRef(0);

  const leftEls = useCallback(
    () =>
      [
        eyebrowRef.current,
        headingRef.current,
        bodyRef.current,
        ctasRef.current,
      ].filter(Boolean),
    [],
  );

  const startProgress = useCallback(() => {
    gsap.set(barRef.current, { width: "0%" });
    gsap.set(ringRef.current, { attr: { "stroke-dashoffset": CIRCUMFERENCE } });
    progTween.current = gsap.timeline();
    progTween.current
      .to(barRef.current, { width: "100%", duration: 5, ease: "none" })
      .to(
        ringRef.current,
        { attr: { "stroke-dashoffset": 0 }, duration: 5, ease: "none" },
        0,
      )
      .call(() => {
        const nextIdx = (curRef.current + 1) % sliderData.length;
        setNext(nextIdx);
      });
  }, []);

  const stopProgress = useCallback(() => {
    if (progTween.current) {
      progTween.current.kill();
      progTween.current = null;
    }
    gsap.set(barRef.current, { width: "0%" });
  }, []);

  const animIn = useCallback(() => {
    gsap.set(leftEls(), { opacity: 0, y: 30 });
    gsap.set(bgRef.current, { scale: 1.1, opacity: 0 });
    const tl = gsap.timeline({
      onComplete: () => {
        setAnimating(false);
        startProgress();
      },
    });
    tl.to(bgRef.current, {
      scale: 1,
      opacity: 1,
      duration: 1,
      ease: "power3.out",
    }).to(
      leftEls(),
      { opacity: 1, y: 0, stagger: 0.1, duration: 0.55, ease: "power3.out" },
      "-=0.55",
    );
  }, [leftEls, startProgress]);

  const animOut = useCallback(
    (cb) => {
      setAnimating(true);
      stopProgress();
      gsap.to(leftEls(), {
        opacity: 0,
        y: -22,
        stagger: 0.06,
        duration: 0.35,
        ease: "power2.in",
        onComplete: cb,
      });
      gsap.to(bgRef.current, {
        scale: 1.06,
        opacity: 0,
        duration: 0.5,
        ease: "power2.in",
      });
    },
    [leftEls, stopProgress],
  );

  const goTo = useCallback(
    (idx) => {
      if (animating || idx === curRef.current) return;
      setNext(idx);
    },
    [animating],
  );

  useEffect(() => {
    if (next === null) return;
    if (next === displayed) {
      setNext(null);
      return;
    }
    animOut(() => {
      curRef.current = next;
      setDisplayed(next);
      setNext(null);
    });
  }, [next]); // eslint-disable-line

  useEffect(() => {
    gsap.set(leftEls(), { opacity: 0, y: 30 });
    gsap.set(bgRef.current, { opacity: 0, scale: 1.1 });
    const t = gsap.delayedCall(0.15, animIn);
    return () => t.kill();
  }, [displayed]); // eslint-disable-line

  const slide = sliderData[displayed];
  const prevIdx = (displayed - 1 + sliderData.length) % sliderData.length;
  const nextIdx = (displayed + 1) % sliderData.length;

  return (
    <div className="sl-wrap">
      <div
        className="sl-bg"
        ref={bgRef}
        style={{ backgroundImage: `url('${slide.image}')` }}
      />
      <div className="sl-scrim" />
      <div className="sl-noise" />

      <div className="sl-grid">
        {/* ── LEFT PANEL ── */}
        <div className="sl-left">
          <div className="sl-eyebrow" ref={eyebrowRef}>
            <span className="sl-ey-num">
              {String(displayed + 1).padStart(2, "0")}
            </span>
            <span className="sl-ey-dash" />
            <span className="sl-ey-txt">{slide.label}</span>
          </div>

          <h1 className="sl-heading" ref={headingRef}>
            <span className="sl-h-line1">{slide.line1}</span>
            <strong className="sl-h-line2">{slide.line2}</strong>
          </h1>

          <p className="sl-body-text" ref={bodyRef}>
            {slide.body}
          </p>

          <div className="sl-ctas" ref={ctasRef}>
            <Link to="/contactus">
              <button className="sl-btn sl-btn-fill">Contact Now</button>
            </Link>
            <button className="sl-btn sl-btn-out">Our Practice</button>
          </div>
        </div>

        {/* ── RIGHT PANEL ── */}
        <div className="sl-right">
          {/* Progress ring */}

          {/* Slide strip */}
          <div className="sl-strip">
            {sliderData.map((d, i) => (
              <div
                key={d.id}
                className={`sl-strip-item${i === displayed ? " active" : ""}`}
                onClick={() => goTo(i)}
              >
                <div className="sl-strip-num">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="sl-strip-title">{d.label}</div>
                <div className="sl-strip-desc">{d.body.slice(0, 72)}…</div>
              </div>
            ))}
          </div>

          {/* Stats + nav */}
          <div className="sl-bottom">
            <div className="sl-stats">
              {slide.stats.map((s, i) => (
                <div className="sl-stat" key={i}>
                  <span className="sl-stat-n">{s.num}</span>
                  <span className="sl-stat-l">{s.label}</span>
                </div>
              ))}
            </div>
            <div className="sl-nav">
              <button
                className="sl-nav-btn"
                onClick={() => goTo(prevIdx)}
                aria-label="Previous slide"
              >
                &#8592;
              </button>
              <button
                className="sl-nav-btn"
                onClick={() => goTo(nextIdx)}
                aria-label="Next slide"
              >
                &#8594;
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="sl-bar" ref={barRef} />
    </div>
  );
};

export default Slider;
